/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/require-await, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/no-misused-promises */

import { merge } from 'lodash';
import { decode } from '@msgpack/msgpack';
import type { FetchAuxOptions } from './types';
import { PollOpts } from 'types/poll';

const TOKEN = import.meta.env.VITE_SIGNED_TOKEN;
const API_HOST = import.meta.env.VITE_SPOTGAMMA_API;
const VERSION = import.meta.env.VITE_VERSION_NUM;

let authHeader = {};
let trendingId: number | NodeJS.Timeout | null = null;

const DEFAULT_OPTS = {
  method: 'GET',
  Origin: '*',
  'X-Requested-With': 'XMLHttpRequest',
  headers: {
    'x-json-web-token': TOKEN,
    'Content-Type': 'application/json',
    Version: VERSION,
  },
};

async function fetchWithRetry(url: string, opts: any = {}) {
  try {
    return await fetch(url, opts);
  } catch (err: any) {
    const defaultMaxRetries = err.message?.includes('Failed to fetch') ? 1 : 0;
    const retryCounter = opts.retryCounter ?? 0;
    if (retryCounter < (opts.maxRetries ?? defaultMaxRetries)) {
      return fetchWithRetry(url, { ...opts, retryCounter: retryCounter + 1 });
    }
    throw err;
  }
}

export function setAuthHeader(header: any) {
  if (header?.headers?.Authorization?.includes('OH_TOKEN')) {
    console.error('Attempting to set OpenHouse token in localStorage:', header);
    return;
  }
  authHeader = header;
}

export async function setBufferPoller(
  endpoint: string,
  interval: number,
  auxOpts: FetchAuxOptions,
  pollerOpts: Partial<PollOpts>,
) {
  const host = auxOpts.host ?? API_HOST;
  const url = `${host}/${endpoint}`;

  const pollOnce = async () => {
    const opts = merge({ ...DEFAULT_OPTS }, { ...authHeader }, auxOpts ?? {}, {
      headers: { 'Polling-Interval-ms': `${interval}` },
    });
    const resp = await fetchWithRetry(url, opts);
    const arrayBuffer = await resp.arrayBuffer();
    const result = {
      id,
      data: pollerOpts?.msgpack ? decode(arrayBuffer) : arrayBuffer,
      status: resp.status,
      headers: Object.fromEntries(resp.headers.entries()),
    };
    self.postMessage(result);
    return result;
  };

  const id = pollerOpts.onlyPollOnce
    ? setTimeout(pollOnce, interval)
    : setInterval(pollOnce, interval);

  return id;
}

export async function setPoller(
  endpoint: string,
  interval: number,
  auxOpts: FetchAuxOptions,
  pollerOpts: Partial<PollOpts>,
) {
  const host = auxOpts.host ?? API_HOST;
  const url = `${host}/${endpoint}`;

  const pollOnce = async () => {
    const opts = merge({ ...DEFAULT_OPTS }, { ...authHeader }, auxOpts ?? {}, {
      headers: { 'Polling-Interval-ms': `${interval}` },
    });
    const resp = await fetchWithRetry(url, opts);
    const json = await resp.json();
    const result = {
      id,
      json,
      status: resp.status,
      headers: Object.fromEntries(resp.headers.entries()),
    };
    self.postMessage(result);
    return result;
  };

  const id = pollerOpts.onlyPollOnce
    ? setTimeout(pollOnce, interval)
    : setInterval(pollOnce, interval);

  return id;
}

export async function setPollerForHiroTrendingAndWatchlist(
  trendingEndpoint: string,
  sparklinesEndpoint: string,
  hiroWatchlist: string[],
  interval: number,
  auxOpts: FetchAuxOptions,
) {
  const trendingUrl = `${API_HOST}/${trendingEndpoint}`;
  const opts = merge({ ...DEFAULT_OPTS }, { ...authHeader }, { ...auxOpts });

  async function pollOnce() {
    try {
      const trendingResponse = await fetchWithRetry(trendingUrl, opts);
      const trendingData = await trendingResponse.json();

      if (!Array.isArray(trendingData)) {
        return null;
      }

      const watchlist = hiroWatchlist.map((sym) => encodeURIComponent(sym));
      const allSyms = trendingData
        .map((d) => encodeURIComponent(d.instrument))
        .filter((s) => !watchlist.includes(s))
        .concat(watchlist);

      const sparklinesLimit = 15;
      let totalSparklinesData = {};

      for (let i = 0; i < allSyms.length; i += sparklinesLimit) {
        const batchedSyms = allSyms.slice(i, i + sparklinesLimit);
        const sparklinesResponse = await fetchWithRetry(
          `${API_HOST}/${sparklinesEndpoint}&syms=${batchedSyms.join('-')}`,
          opts,
        );
        const sparklinesData = await sparklinesResponse.json();
        totalSparklinesData = { ...totalSparklinesData, ...sparklinesData };
      }

      const result = {
        id,
        response: {
          trending: trendingData,
          sparklines: totalSparklinesData,
        },
      };
      self.postMessage(result);
      return result;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  clearInterval(trendingId!);
  const id = setInterval(pollOnce, interval);
  trendingId = id;
  return id;
}

export function clearPoller(id: number | NodeJS.Timeout) {
  clearInterval(id);
  clearTimeout(id);
}
